"use client";
import Image from "next/image";
import Container from "./container";
import { Separator } from "./ui/separator";
import imrsiv from "@/public/images/imersiv logo-09.png";
import facebook from "@/public/images/social-media-icons/facebook.svg";
import instagram from "@/public/images/social-media-icons/instagram.svg";
import snapchat from "@/public/images/social-media-icons/snapchat.svg";
import tiktok from "@/public/images/social-media-icons/tiktok.svg";
import linkedin from "@/public/images/social-media-icons/linkedin.svg";
import X from "@/public/images/social-media-icons/x.svg";
import bigLogo from "@/public/images/big logo.svg";
import { useTranslations } from "next-intl";
import LinkNext from "next/link";
import { unitTypes } from "@/lib/enums";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { useParams, usePathname } from "next/navigation";
import { useCookieConsent } from "./analytics/CookieConsentContext";

const Footer = () => {
  const t = useTranslations();
  const { resetConsent } = useCookieConsent();
  const path = usePathname();
  const { unit } = useParams();
  return (
    <footer
      className={cn("pt-7 sm:pt-14 pb-1 bg-green-background *:text-gray-dark", {
        "max-sm:mb-24": path === `/all-units/${unit}`,
      })}
    >
      <Container>
        <div className="relative w-fit">
          <Image width={150} height={0} src={bigLogo.src} alt="big logo" />
        </div>
        <div className="mt-2 flex flex-col border border-green-main rounded-lg p-1 w-fit">
          <span>{t("license")}</span>
          <span>{t("licenseNo")}</span>
          <span>{t("commercialRegNo")}</span>
        </div>
      </Container>
      <Container className="mt-7 sm:mt-12 grid grid-cols-2 lg:grid-cols-6 justify-between gap-y-5 sm:gap-10 *:text-sm *:space-y-3">
        <p className="max-sm:col-span-2 col-span-2 text-justify leading-loose">
          {t("footer.description")}
        </p>
        <div className="col-span-2 flex justify-center *:flex-1 sm:gap-10 *:space-y-3">
          <ul>
            <li>
              <Link
                className="hover:underline"
                prefetch
                href={{
                  pathname: "/all-units",
                  query: { classification: unitTypes.hut },
                }}
              >
                {t("navbar.items.woodenCabins")}
              </Link>
            </li>
            <li>
              <Link
                className="hover:underline"
                prefetch
                href={{
                  pathname: "/all-units",
                  query: { classification: unitTypes.resort },
                }}
              >
                {t("navbar.items.resort")}
              </Link>
            </li>
            <li>
              <Link
                className="hover:underline"
                prefetch
                href={{
                  pathname: "/all-units",
                  query: { classification: unitTypes.chalet },
                }}
              >
                {t("navbar.items.chalets")}
              </Link>
            </li>
            <li>
              <Link
                className="hover:underline"
                prefetch
                href={{
                  pathname: "/all-units",
                  query: { classification: unitTypes.apartment },
                }}
              >
                {t("navbar.items.furnishedApartments")}
              </Link>
            </li>
            <li>
              <Link
                className="hover:underline"
                prefetch
                href={{
                  pathname: `/all-units`,
                  query: { classification: unitTypes.room },
                }}
              >
                {t("navbar.items.hotelRoom")}
              </Link>
            </li>
          </ul>
          <ul className="!mt-0">
            <Link
              href={"/terms-and-conditions"}
              className="list-item hover:underline"
              prefetch
            >
              {t("completeProfileSection.terms")}
            </Link>
            {process.env.NEXT_PUBLIC_BUSINESS_URL && (
              <LinkNext
                className="list-item hover:underline"
                href={process.env.NEXT_PUBLIC_BUSINESS_URL}
              >
                {t("footer.addProperty")}
              </LinkNext>
            )}
            <Link
              prefetch
              href={"/contact-us"}
              className="list-item hover:underline"
            >
              {t("footer.contactUs")}
            </Link>
            {/* Reopens the consent banner. text-start because a bare <button>
                centres its label where the sibling <Link>s do not, which is
                visible under RTL. */}
            <button
              type="button"
              onClick={resetConsent}
              className="list-item hover:underline text-start"
            >
              {t("cookies.manage")}
            </button>
          </ul>
        </div>
        <ul className="max-lg:col-span-2 *:whitespace-nowrap">
          <li>{t("footer.location")}</li>
          <li>
            <a href="tel:+9660590700029" dir="ltr">
              0590700029
            </a>
          </li>
          <li>
            <a href="mailto:admin@ehjzlee.com">admin@ehjzlee.com</a>
          </li>
        </ul>
      </Container>
      <Container>
        <div className="my-2.5 min-h-5 flex flex-row-reverse gap-2 sm:gap-4">
          <a href="https://www.linkedin.com/company/ehjzlee-/" target="_blank">
            <Image
              src={linkedin}
              alt="linkedin"
              className="size-5 object-cover object-center"
            />
          </a>
          <a href="https://www.facebook.com/EhjzLee.KSA" target="_blank">
            <Image
              src={facebook}
              alt="facebook"
              className="size-5 object-cover object-center"
            />
          </a>
          <a href="https://www.instagram.com/ehjzlee/" target="_blank">
            <Image
              src={instagram}
              alt="instagram"
              className="size-5 object-cover object-center"
            />
          </a>
          <a href="https://www.snapchat.com/add/ehjzlee" target="_blank">
            <Image
              src={snapchat}
              alt="snapchat"
              className="size-5 object-cover object-center"
            />
          </a>
          <a
            href="https://www.tiktok.com/@ehjzlee?_t=8s85a7QeAFt&_r=1"
            target="_blank"
          >
            <Image
              src={tiktok}
              alt="tiktok"
              className="size-5 object-cover object-center"
            />
          </a>
          <a
            href="https://x.com/EhjzLee?t=evCIRZddvKftBhNzhnc0Yg&s=09"
            target="_blank"
          >
            <Image
              src={X}
              alt="X"
              className="size-5 object-cover object-center"
            />
          </a>
        </div>
        <Separator className="after:bg-[#303E48] before:bg-[#303E48]" />
        <p className="pb-4 text-xs sm:text-sm">
          {t("footer.rights", { year: new Date().getFullYear() })}{" "}
        </p>
        <p className="mb-2 text-xs sm:text-sm flex justify-center items-center gap-1">
          {t("footer.poweredBy")}{" "}
          <a
            href="https://www.imrsiv.co"
            target="_blank"
            className="hover:underline flex items-center gap-1"
          >
            <Image src={imrsiv} className="size-7" alt="imrsiv logo" />
            IMRSIV
          </a>
        </p>
      </Container>
    </footer>
  );
};

export default Footer;
